/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import _ from 'lodash';
import can from 'can';
import Construct from 'can/construct/construct';
import FeedTransport from 'scripts/transports/feed-transport';
import SimulationFeedTransport from 'scripts/transports/simulation-feed-transport';
import dispatcher from 'scripts/services/simulation-dispatcher';
import dayjs from 'dayjs';

const REQUEST_RATE = 30000;

export default Construct.extend({}, can.event).extend({
    fromCache: true,
    feedUrl: '',
    headers: {},

    init({ feedUrl, fromCache, autoUpdate, requestRate, headers }) {
        if (!requestRate) {
            requestRate = REQUEST_RATE;
        }

        this.requestRate = Number(requestRate) || REQUEST_RATE;
        this.feedUrl = feedUrl || this.feedUrl;
        this.fromCache = _.isBoolean(fromCache) ? fromCache : this.fromCache;
        this.params = null;
        this.isFirstRequest = true;
        this.firstRequestRun = can.Deferred();
        this.autoFetchInterval = null;
        this.autoFetchers = {};
        this.autoFetching = autoUpdate === undefined || autoUpdate === true;
        this.headers = headers || {};

        if (this.autoFetching) {
            this.currentSimulationDate = null;
            this.simulationState = 'stop';
            this.subscribedToDispatcher = false;
            this.dispatcherConfigInitializeHandler = this.onDispatcherConfigInitialize.bind(
                this,
            );
            this.dispatcherConfigChangeHandler = this.onDispatcherConfigChange.bind(
                this,
            );

            dispatcher.on(
                'configInitialize',
                this.dispatcherConfigInitializeHandler,
            );
            dispatcher.on('configChange', this.dispatcherConfigChangeHandler);

            this.autoFetchInterval = setInterval(
                () => this.autoFetch(),
                this.requestRate,
            );
        }
    },

    destroy() {
        if (this.autoFetching) {
            this.autoFetchers = {};
            clearInterval(this.autoFetchInterval);
            dispatcher.off(
                'configInitialize',
                this.dispatcherConfigInitializeHandler,
            );
            dispatcher.off('configChange', this.dispatcherConfigChangeHandler);
        }
    },

    onDispatcherConfigInitialize(ev, config) {
        if (this.subscribedToDispatcher) {
            return;
        }
        this.subscribedToDispatcher = true;

        this.processDispatcherEvent(config);
    },

    onDispatcherConfigChange(ev, config) {
        this.processDispatcherEvent(config);
    },

    processDispatcherEvent(config) {
        if (config.state === this.simulationState) {
            return;
        }
        this.simulationState = config.state;

        if (config.state === 'start') {
            this.currentSimulationDate = config.currentDate || config.startDate;
            this.startSimulation(config);
        } else if (config.state === 'pause') {
            this.pauseSimulation();
        } else {
            this.currentSimulationDate = null;
            this.stopSimulation();
        }
    },

    fetch(params) {
        if (this.autoFetching) {
            this.addAutoFetcher('default', this.fetch.bind(this));
        }
        this.params = params || this.params;
        if (this.isFirstRequest) {
            this.dispatch('fetching', [true]);
            this.isFirstRequest = false;
            this.firstRequestRun.resolve();
        }
        if (this.simulationState === 'start') {
            return;
        }

        FeedTransport.request(
            { url: this.getUrl(this.params), headers: this.headers },
            this.fromCache,
        )
            .then((data) => {
                this.dispatch('data', [data]);
                this.dispatch('fetching', [false]);
            })
            .fail((error) => {
                this.dispatch('error', [error]);
            });
    },

    fetchSimulation(simulationConfig) {
        this.removeAutoFetcher('default');
        const label =
            this.getUrl(this.params) +
            ' ' +
            dayjs(this.currentSimulationDate).toISOString();
        console.time(label);
        SimulationFeedTransport.request(
            this.getUrl(this.params),
            simulationConfig,
            this.currentSimulationDate,
        ).then((data, status, xhr) => {
            console.timeEnd(label);
            const date = dayjs(
                xhr.getResponseHeader(
                    SimulationFeedTransport.REPLAY_TIMESTAMP_KEY,
                ),
            );

            if (date.isValid()) {
                dispatcher.dispatch('currentDateChange', [
                    this.currentSimulationDate,
                ]);
                this.currentSimulationDate +=
                    this.requestRate * (simulationConfig.timeScaleFactor || 1);

                this.dispatch('data', [data]);
                this.dispatch('fetching', [false]);
            }
        });
    },

    startSimulation(simulationConfig) {
        console.log(`${this.feedUrl} simulation start`);
        this.addAutoFetcher(
            'simulation',
            this.fetchSimulation.bind(this, simulationConfig),
        );
        this.firstRequestRun.then(() => this.fetchSimulation(simulationConfig));
    },

    pauseSimulation() {
        console.log(`${this.feedUrl} simulation paused`);
        this.removeAutoFetcher('simulation');
    },

    stopSimulation() {
        console.log(`${this.feedUrl} simulation stop`);
        this.removeAutoFetcher('simulation');
        this.addAutoFetcher('default', this.fetch.bind(this));
        this.autoFetch();
    },

    autoFetch() {
        if (!this.isFirstRequest) {
            _.each(this.autoFetchers, (autoFetcher) => {
                if (_.isFunction(autoFetcher)) {
                    autoFetcher();
                }
            });
        }
    },

    addAutoFetcher(type, cb) {
        this.autoFetchers[type] = cb;
    },

    removeAutoFetcher(type) {
        _.unset(this.autoFetchers, type);
    },

    getUrl(params) {
        if (params) {
            return can.sub(this.feedUrl, params);
        } else {
            return this.feedUrl;
        }
    },
});
